import type { components } from '@connected/api-client/api.generated';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Label } from '@connected/form-fields/components/label';
import {
    SingleHorizontalOptionItem,
    SingleOptionField,
} from '@connected/form-fields/components/option';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useTranslation } from 'react-i18next';

import { ObservationComponent } from '@/components/features/observation';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/final-circuits/$finalCircuitId/checklist/',
)({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
            electricalProjectVersionId: params.projectVersionId,
            electricalProjectFinalCircuitId: params.finalCircuitId,
        }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
});

function Page() {
    const { t } = useTranslation('re-inspections');
    const { t: c } = useTranslation('common');

    const params = Route.useParams();

    const { data: finalCircuitData } = useQuery(
        elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
            electricalProjectFinalCircuitId: params.finalCircuitId,
        }),
    );

    const patcher = usePatcher('/api/v2/electrical/projects/finalcircuits/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables?.body);

            const finalCircuitQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                electricalProjectFinalCircuitId: params.finalCircuitId,
            });

            await queryClient.cancelQueries(finalCircuitQueryOptions);

            const previousFinalCircuitData = queryClient.getQueryData(finalCircuitQueryOptions.queryKey) as components['schemas']['EditElectricalProjectFinalCircuitQueryResult'];
            const newFinalCircuitData: components['schemas']['EditElectricalProjectFinalCircuitQueryResult'] = {
                ...previousFinalCircuitData,
                Circuit: {
                    ...previousFinalCircuitData.Circuit,
                    ...body,
                    ElectricalProjectFinalCircuitId: params.finalCircuitId,
                },
            };

            queryClient.setQueryData(finalCircuitQueryOptions.queryKey, newFinalCircuitData);

            return { previousFinalCircuitData, finalCircuitQueryOptions };
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.finalCircuit.finalCircuitOptions({
                    electricalProjectFinalCircuitId: params.finalCircuitId,
                }),
            );
        },
        scope: {
            id: params.projectVersionId,
        },
    }, {
        body: {
            ElectricalProjectFinalCircuitId: params.finalCircuitId,
        },
    });

    return (
        <div className="flex flex-col gap-4">
            <Container>
                <Container.Header className="flex-row justify-between items-center">
                    <Container.Title>
                        {t('final-circuit.layout.navigation.checklist.label')}
                    </Container.Title>
                    <label className="flex items-center gap-2 text-sm font-normal cursor-pointer">
                        <Checkbox
                            checked={finalCircuitData?.Circuit?.ReportCheckListItems !== false}
                            onCheckedChange={(checked) => {
                                patcher.patchField('ReportCheckListItems')({ value: checked });
                            }}
                        />
                        {c('report.include-in-report')}
                    </label>
                </Container.Header>
                <Container.Content>
                    <Form />
                </Container.Content>
                <Section.Footer>
                    <FooterNavigation />
                </Section.Footer>
            </Container>
        </div>
    );
}

type CheckListItemFormValues = {
    conclusionKind: components['schemas']['ListElectricalProjectCheckListItemQueryResultItem']['ConclusionKind'];
};

function Form() {
    const params = Route.useParams();

    const { data } = useQuery(elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
        electricalProjectFinalCircuitId: params.finalCircuitId,
        electricalProjectVersionId: params.projectVersionId,
    }));

    const checkListItemsData = data?.Items || [];

    return (
        <div className="flex flex-col gap-4">
            {checkListItemsData.map(item => (
                <ChecklistItem
                    key={item.ElectricalCheckListItemId}
                    item={item}
                />
            ))}
        </div>
    );
}
function ChecklistItem({
    item,
}: {
    item: components['schemas']['ListElectricalProjectCheckListItemQueryResultItem'];
}) {
    const params = Route.useParams();

    const checkListItemMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/checklists/items/edit',
        {
            onMutate: async (variables) => {
                const checkListQueryOptions = elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
                    electricalProjectVersionId: params.projectVersionId,
                    electricalProjectFinalCircuitId: params.finalCircuitId,
                });

                const previousDataList = queryClient.getQueryData(checkListQueryOptions.queryKey) as components['schemas']['ListElectricalProjectCheckListItemQueryResult'];

                await queryClient.cancelQueries(checkListQueryOptions);

                queryClient.setQueryData<components['schemas']['ListElectricalProjectCheckListItemQueryResult']>(
                    checkListQueryOptions.queryKey,
                    (old) => {
                        if (old) {
                            const updatedData: components['schemas']['ListElectricalProjectCheckListItemQueryResult'] = {
                                ...old,
                                Items: old.Items.map(checklistItem =>
                                    checklistItem.ElectricalProjectCheckListItemId === variables!.body!.ElectricalProjectCheckListItemId
                                        ? { ...checklistItem, ConclusionKind: variables!.body!.ConclusionKind }
                                        : checklistItem,
                                ),
                            };
                            return updatedData;
                        }
                        return old;
                    },
                );

                return { previousDataList };
            },
            onSettled: async () => {
                await queryClient.invalidateQueries(elektraAppQueryOptions.project.inspection.finalCircuit.checkListItemsOptions({
                    electricalProjectVersionId: params.projectVersionId,
                    electricalProjectFinalCircuitId: params.finalCircuitId,
                }));
            },
            scope: {
                id: params.projectVersionId,
            },
        },
    );

    const form = useForm<CheckListItemFormValues, ZodValidator>({
        defaultValues: {
            conclusionKind: item.ConclusionKind || null,
        },
        validatorAdapter: zodValidator(),
        onSubmit: async ({ value }) => {
            await checkListItemMutation.mutateAsync({
                body: {
                    ElectricalProjectCheckListItemId:
                        item.ElectricalProjectCheckListItemId!,
                    ConclusionKind: value.conclusionKind,
                },
            });
        },
    });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await form.handleSubmit();
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <form.Field name="conclusionKind">
                {({ handleChange, state, name }) => (
                    <Label label={item.Description} htmlFor={name}>
                        <SingleOptionField
                            name={name}
                            handleChange={(e) => {
                                handleChange(e);
                                form.handleSubmit();
                            }}
                            state={state}
                            className="flex flex-row items-center justify-between"
                            options={[
                                {
                                    label: 'Akkoord',
                                    value: 'Approved',
                                },
                                {
                                    label: 'Niet akkoord',
                                    value: 'NotApproved',
                                },
                                {
                                    label: 'N.V.T.',
                                    value: 'NotApplicable',
                                },
                            ]}
                            render={SingleHorizontalOptionItem}
                        />
                    </Label>
                )}
            </form.Field>
            <form.Subscribe selector={({ values }) => [values.conclusionKind]}>
                {([conclusionKind]) => (
                    <div>
                        {conclusionKind === 'NotApproved' && (
                            <div>
                                <ObservationComponent
                                    electricalProjectVersionId={params.projectVersionId}
                                    objectId={item.ElectricalProjectCheckListItemId!}
                                    objectKind="CheckListItem"
                                    checkListItemKind="FinalCircuit"
                                />
                            </div>
                        )}
                    </div>
                )}
            </form.Subscribe>
        </form>
    );
}
